import { HttpResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { ConfigurationService } from '@sondermind/configuration';
import { SondermindHttpService } from '@sondermind/http-deprecated';
import { ServerResponse } from '@sondermind/utilities/models-http';
import { Patient } from '@sondermind/utilities/models-patient';

export enum PatientIndexSortOrder {
  DESC = -1,
  ASC = 1
}

export interface PatientIndexOptions {
  status: 'active' | 'inactive';
  search: string;
  sorting: {
    sort_by: string;
    sort_order: PatientIndexSortOrder;
  };
  pagination: {
    page: number;
    per_page: number;
  };
}

@Injectable({
  providedIn: 'root'
})
export class PatientService {
  // Searching sorting parameters
  per_page = 15;
  page: number;
  filter: string;
  sort_by = 'patient_name';
  sort_order: any = 'asc';

  constructor(
    private http: SondermindHttpService,
    private configurationService: ConfigurationService
  ) { }

  update(user_id: any, patient: Patient): Observable<ServerResponse<Patient>> {
    let search = new HttpParams();
    search = search.set('user_id', user_id.toString());

    const options = this.http.authRequestOptions();
    options.params = search;

    const params = {
      patient: {
        first_name: patient.firstName,
        last_name: patient.lastName,
        middle_name: patient.middleName,
        home_phone: patient.homePhone,
        cell_phone: patient.cellPhone,
        address_line1: patient.addressLine1,
        address_line2: patient.addressLine2,
        city: patient.city,
        state: patient.state,
        postal_code: patient.postalCode,
        email: patient.email,
        date_of_birth: patient.dateOfBirth,
        gender_id: patient.genderId,
        patient_ethnicities_attributes: patient.ethnicities,
        marital_status_id: patient.maritalStatusId
      }
    };

    return this.http
      .patch<any>(['/patients/:id.json', { id: patient.id }], params, options)
      .pipe(
        map((res) => this.processSingleResponse(res)),
        catchError((res) => this.http.handleErrorObject(res, 'patient'))
      );
  }

  updateGender(patientId: number, genderId: number): Observable<ServerResponse<Patient>> {
    const params = {
      patient: {
        gender_id: genderId
      }
    };

    return this.http
      .patch<any>(['/patients/:id.json', { id: patientId }], params)
      .pipe(
        map((res) => this.processSingleResponse(res)),
        catchError((res) => this.http.handleErrorObject(res, 'patient'))
      );
  }

  create(userId: any, patient: Patient): Observable<ServerResponse<Patient>> {
    let search = new HttpParams();
    search = search.set('user_id', userId.toString());

    const options = this.http.authRequestOptions();
    options.params = search;

    const params = {
      patient: {
        first_name: patient.firstName,
        last_name: patient.lastName,
        preferred_first_name: patient.preferredFirstName,
        home_phone: patient.homePhone,
        cell_phone: patient.cellPhone,
        email: patient.email,
        date_of_birth: patient.dateOfBirth,
        gender_id: patient.genderId,
        non_spn_patient: patient.nonSpnPatient
      }
    };

    return this.http
      .post<any>('/patients.json', params, options)
      .pipe(
        map((res) => this.processSingleResponse(res)),
        catchError((res) => this.http.handleErrorObject(res, 'patient'))
      );
  }

  load(patientId: number | string): Observable<ServerResponse<Patient>> {
    const options = this.http.authRequestOptions();

    return this.http
      .get<any>(['/patients/:id.json', { id: patientId }], options)
      .pipe(
        map((res) => this.processSingleResponse(res)),
        catchError((res) => this.http.handleErrorObject(res, 'patient'))
      );
  }

  index(userId: any, options: Partial<PatientIndexOptions>): Observable<ServerResponse<Patient[]>> {
    const httpOptions = this.http.authRequestOptions();
    httpOptions.params = new HttpParams();
    httpOptions.params = httpOptions.params.set('user_id', userId.toString());
    httpOptions.params = httpOptions.params.set('status', options.status || 'active');
    if (options.search) {
      httpOptions.params = httpOptions.params.set('search', options.search);
    }
    if (options.sorting) {
      httpOptions.params = httpOptions.params.set('sort_by', options.sorting.sort_by);
      httpOptions.params = httpOptions.params.set('sort_order', options.sorting.sort_order.toString());
    }
    if (options.pagination) {
      httpOptions.params = httpOptions.params.set('page', options.pagination.page.toString());
      httpOptions.params = httpOptions.params.set('per_page', options.pagination.per_page.toString());
    } else {
      httpOptions.params = httpOptions.params.set('all', 'true');
    }

    return this.http
      .get<any>('/patients.json', httpOptions)
      .pipe(
        map((res) => this.processResponse(res)),
        catchError((res) => this.http.handleErrorObject(res, 'patients'))
      );
  }

  all(
    userId: any,
    all: boolean = false,
    status: string = 'active'
  ): Observable<ServerResponse<Patient[]>> {
    const options: Partial<PatientIndexOptions> = {};

    if (status === 'active' || status === 'inactive') {
      options.status = status;
    } else {
      // eslint-disable-next-line no-console
      console.error('invalid patient search status string:', status);
      (<any>options.status) = status;
    }

    if (all === false) {
      options.pagination = {
        page: this.page || 1,
        per_page: this.per_page || 150,
      };

      if (this.filter) {
        options.search = this.filter;
      }

      if (this.sort_by && this.sort_order) {
        options.sorting = {
          sort_by: this.sort_by,
          sort_order: this.sort_order === 'desc' ? PatientIndexSortOrder.DESC : PatientIndexSortOrder.ASC
        };
      }
    } else {
      // all = true
      options.pagination = null;
    }

    return this.index(userId, options);
  }

  activeInactive(userId: any, patientId: number, active = true): Observable<ServerResponse<Patient>> {
    let search = new HttpParams();
    search = search.set('user_id', userId.toString());

    const options = this.http.authRequestOptions();
    options.params = search;

    return this.http
      .patch<any>(['/patients/:id/set_active_inactive.json', { id: patientId }], { active }, options)
      .pipe(
        map((res) => this.processSingleResponse(res)),
        catchError((res) => this.http.handleErrorObject(res, 'patient'))
      );
  }

  toggleNonSpnPatient(userId: any, patient: Patient): Observable<ServerResponse<Patient>> {
    let search = new HttpParams();
    search = search.set('user_id', userId.toString());

    const options = this.http.authRequestOptions();
    options.params = search;

    return this.http
      .patch<any>(['/patients/:id/toggle_non_spn_patient.json', { id: patient.id }], {}, options)
      .pipe(
        map((res) => this.processSingleResponse(res)),
        catchError((res) => this.http.handleErrorObject(res, 'patient'))
      );
  }

  sendInvitation(userId: any, patient: Patient, data: any): Observable<ServerResponse<Patient>> {
    let search = new HttpParams();
    search = search.set('user_id', userId.toString());

    const options = this.http.authRequestOptions();
    options.params = search;

    const params = data ? {
      patient: {
        email: data.email,
        last_name: data.lastName,
        date_of_birth: data.dateOfBirth
      }
    } : null;

    return this.http
      .post<any>(['/patients/:id/send_invitation.json', { id: patient.id }], params, options)
      .pipe(
        map((res) => this.processSingleResponse(res)),
        catchError((res) => this.http.handleErrorObject(res, 'patient'))
      );
  }

  private processSingleResponse(res: HttpResponse<any>): ServerResponse<Patient> {
    const serverResponse = new ServerResponse(res, 'patient');
    const patient = new Patient();
    patient.load_from_json(serverResponse.value);
    serverResponse.value = patient;
    return serverResponse;
  }

  private processResponse(res: HttpResponse<any>): ServerResponse<Patient[]> {
    const serverResponse = new ServerResponse(res, 'patients');
    const patients = [];

    // eslint-disable-next-line no-restricted-syntax, guard-for-in
    for (const index in serverResponse.value) {
      const mjson = serverResponse.value[index];
      const patient = new Patient();
      patient.load_from_json(mjson);
      patients.push(patient);
    }

    serverResponse.value = patients;
    return serverResponse;
  }
}
